import React, { useEffect, useMemo, useRef, useState } from 'react';
import isEqual from 'lodash/isEqual';
import { hasBoqEntryChanges } from 'v2/apps/shared/components/boq/boqDraftChanges';
import i18next from 'i18next';
import { connect } from 'react-redux';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import Skeleton from '@mui/material/Skeleton';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { CONSTANTS } from 'clink-components';
import { useContext } from 'hooks/context';
import {
  fetchGenerateBoqStatus,
  fetchRefineBoqStatus,
  refineBoqWithAI,
  clearAiGenerationResult,
  clearAiGenerationForPackage,
  acceptRefineRow,
  rejectRefineRow,
  acceptAllRefineRows,
  rejectAllRefineRows,
  clearRefineBoqForPackage,
} from 'v2/store/reducers/common/boq';
import DataGrid from 'v2/apps/shared/components/boq/data-grid';
import columnsGrid from 'v2/apps/shared/components/boq/ClinkConfig';
import Modal from 'v2/apps/clink/pages/orders/subcontractors/modal';
import Wrapper from './Wrapper';
import {
  boqContentHeaderActionsSx,
  boqHeaderTitleSx,
  boqHeaderTitleTypographySx,
  getBoqStatusChipSx,
  boqEditButtonSx,
  boqNewButtonSx,
  boqPublishButtonSx,
  boqAiInlineAlertBannerSx,
  boqAiInlineAlertIconBoxSx,
  boqAiInlineAlertTextColSx,
  boqAiInlineAlertTitleTypographySx,
  boqAiInlineAlertBodyTypographySx,
  boqAiInlineAlertActionsSx,
  boqAiServiceErrorPanelWrapperSx,
  boqAiServiceErrorCardSx,
  boqAiServiceErrorIconCircleSx,
  boqAiServiceErrorTextColSx,
  boqAiServiceErrorBadgeSx,
  boqAiServiceErrorTitleTypographySx,
  boqAiServiceErrorSubtitleTypographySx,
  boqAiServiceErrorSuggestionsHeadingSx,
  boqAiServiceErrorSuggestionsListSx,
  boqAiServiceErrorSuggestionsWrapSx,
  boqAiServiceErrorRetryRowSx,
  boqAiServiceErrorRetryButtonSx,
  getBoqContentDataGridSx,
  boqCardSectionSx,
  inputBaseSx,
  modalSx,
  newBoqModalCancelSx,
  newBoqModalAcceptSx,
  newBoqModalTitleSx,
} from './style';
import { selectBoqUiLoadingFlags } from 'v2/apps/clink/pages/boq/selectBoqUiLoadingFlags';
import Footer from './Footer';
import NoteTextarea from './NoteTextarea';
import BoqStatusNotice from './BoqStatusNotice';
import RefinePromptCard from './RefinePromptCard';
import RefineChangesBanner from './RefineChangesBanner';
import RefineSummaryBanner from './RefineSummaryBanner';
import buildRefineColumns from './buildRefineColumns';
import {
  buildBoqEntityUpdatePayload,
  buildNewBoqUpdatePayload,
  mapAiResultEntriesToBoqDraftRows,
  mapAiNoteToDraft,
  hasActiveBoqRows,
} from 'v2/apps/clink/pages/boq/boqEntityPayload';
import {
  BOQ_STATUS,
  resolveBoqStatus,
} from 'v2/apps/clink/pages/boq/boqStatus';
import {
  estimateBoqRowHeight,
} from 'v2/apps/shared/components/boq/boqLineText';
import {
  AI_LIFECYCLE_STATUS,
  isAiLifecycleFailure,
  normalizeAiLifecycleStatus,
} from 'v2/apps/clink/pages/boq/boqAiStatus';
import {
  REFINE_ANALYSED_STATE_CODES,
  REFINE_BOQ_STATE_CODE,
  REFINE_RETRYABLE_STATE_CODES,
  isRefinePromptReady,
  isRefineValidationError,
  isUserFacingRefineErrorMessage,
} from 'v2/apps/clink/pages/boq/boqRefinement';

const ITEM = 'item';
const SECTION = 'section';
const GROUPED_HEADING = 'grouped_heading';
const DELETE_STATUS = 4;
const AI_POLL_INITIAL_DELAY_MS = 1000;
const AI_POLL_INTERVAL_MS = 5000;
const REFINE_POLL_INITIAL_DELAY_MS = 1000;
const REFINE_POLL_INTERVAL_MS = 5000;
const sectionAcceptedColumns = ['actions'];
const acceptedColumns = [
  '__reorder__',
  'item_no',
  'description',
  ...sectionAcceptedColumns,
];

const Content = ({
  boq,
  contextType = 'clink',
  slug = '',
  entity,
  theme = {},
  dispatch,
  enable = false,
}) => {
  const context = useContext(contextType);
  const [open, setOpen] = useState(false);
  const [refinePrompt, setRefinePrompt] = useState('');

  const { actions } = context;
  const { units, projectStatuses, readyAfterTemplate } = boq;
  const {
    entries = [],
    nextEntries = [],
    id,
    has_published_version,
    editing,
    nextNote,
    note,
    saved,
  } = entity;

  const {
    boqAccent,
    boqSectionBg,
    borderbox,
    clinkLightPurple,
    eerieBlack,
    darkCharcoal,
    white,
    black,
    clinkRed,
  } = CONSTANTS.colors.general;

  const packageId = id;
  const boqStatus = resolveBoqStatus(entity, projectStatuses);
  const isDraftState = boqStatus.value === BOQ_STATUS.DRAFT;
  const effectiveEditing = Boolean(editing && boqStatus.canMutate);
  const aiStatus = boq?.aiGenerationById?.[packageId];
  const refineJob = boq?.refineById?.[packageId];
  const refineReview = boq?.refineReviewById?.[packageId];
  const refineReviewRows = useMemo(
    () => (Array.isArray(refineReview?.rows) ? refineReview.rows : []),
    [refineReview?.rows]
  );
  const hasPendingRefineReview = refineReviewRows.length > 0;
  const isRefineInProgress =
    refineJob?.stateCode === REFINE_BOQ_STATE_CODE.PENDING ||
    refineJob?.stateCode === REFINE_BOQ_STATE_CODE.STARTED;
  const pendingRefineChanges = useMemo(
    () =>
      refineReviewRows.reduce((changes, row) => {
        if (row?.rowId == null) return changes;
        changes[String(row.rowId)] = {
          fields: (row.changedCells || []).reduce((fields, cell) => {
            fields[cell.field] = {
              before: cell.before,
              after: cell.after,
            };
            return fields;
          }, {}),
        };
        return changes;
      }, {}),
    [refineReviewRows]
  );
  const {
    isBoqListLoading,
    isAiPollLoading,
    isEntityUpdating,
    isNewBoqResetting,
    isPublishing,
    isBoqCreationBusy,
  } = selectBoqUiLoadingFlags(boq, packageId);
  const showBoqGridSkeleton = Boolean(
    enable &&
      (isBoqListLoading ||
        isAiPollLoading ||
        isPublishing ||
        (isEntityUpdating && !isNewBoqResetting))
  );
  const aiLifecycleStatus = normalizeAiLifecycleStatus(aiStatus?.status);
  const isAiInProgress =
    aiLifecycleStatus === AI_LIFECYCLE_STATUS.PENDING ||
    aiLifecycleStatus === AI_LIFECYCLE_STATUS.STARTED;
  const isAiNotFound = aiLifecycleStatus === AI_LIFECYCLE_STATUS.NOT_FOUND;
  const isSmartBoqInProgress = isAiInProgress;
  const headerActionsDisabled =
    isSmartBoqInProgress ||
    isRefineInProgress ||
    hasPendingRefineReview ||
    isBoqCreationBusy ||
    isPublishing;
  const isAiSuccess = aiLifecycleStatus === AI_LIFECYCLE_STATUS.SUCCESS;
  const isAiError = aiLifecycleStatus === AI_LIFECYCLE_STATUS.ERROR;
  /** GET ai/generate-boq: invalid/corrupt file returns `status: "FAILURE"`. */
  const isAiFailure = isAiLifecycleFailure(aiLifecycleStatus);
  /** Transport/POST failures (not FAILURE); full-page error when no saved rows. */
  const hasBoqTableRows = Boolean(entries?.length || nextEntries?.length);
  const isAiErrorBlockingBoq = isAiError && !hasBoqTableRows;

  const aiResultEntries = aiStatus?.result?.entries;
  const hasMeasurableItems =
    Array.isArray(aiResultEntries) &&
    aiResultEntries.some(
      (e) => String(e?.type || '').toLowerCase().trim() === 'item'
    );
  const isAiEmptyResult =
    isAiSuccess && Array.isArray(aiResultEntries) && !hasMeasurableItems;
  /** FAILURE or empty SUCCESS: header shows only New BoQ. */
  const isSmartBoqFailedResult = isAiFailure || isAiEmptyResult;

  const progressMeta = useMemo(() => {
    const total = Number(aiStatus?.total_steps || 0);
    const current = Number(aiStatus?.current_step || 0);
    const pct =
      total > 0 ? Math.min(100, Math.max(0, Math.floor((current / total) * 100))) : 0;
    return { total, current, pct };
  }, [aiStatus?.total_steps, aiStatus?.current_step]);

  const hasAppliedAiResultRef = useRef(false);
  const pollIntervalRef = useRef(null);
  const pollTimeoutRef = useRef(null);
  const refinePollIntervalRef = useRef(null);
  const refinePollTimeoutRef = useRef(null);
  const clearedRefinePromptRequestIdRef = useRef(null);
  const isAiInProgressRef = useRef(isAiInProgress);
  const isRefineInProgressRef = useRef(isRefineInProgress);
  const aiPollGenerationRef = useRef(boq?.aiPollGenerationById?.[packageId] ?? 0);
  const refinePollGenerationRef = useRef(
    boq?.refinePollGenerationById?.[packageId] ?? 0
  );
  isAiInProgressRef.current = isAiInProgress;
  isRefineInProgressRef.current = isRefineInProgress;
  aiPollGenerationRef.current = boq?.aiPollGenerationById?.[packageId] ?? 0;
  refinePollGenerationRef.current =
    boq?.refinePollGenerationById?.[packageId] ?? 0;

  useEffect(() => {
    setRefinePrompt('');
  }, [packageId]);

  const stopAiPolling = () => {
    isAiInProgressRef.current = false;
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  const palette = theme ? theme.palette : {};

  const hasEntryChanges = hasBoqEntryChanges(
    entity?.nextEntries,
    entity?.entries
  );
  const hasNoteChanges = !isEqual(entity?.nextNote, entity?.note);

  const hasChanges = readyAfterTemplate || hasNoteChanges || hasEntryChanges;

  const totalPrice = nextEntries.reduce(
    (total, entry) =>
      entry.type === 'item'
        ? (Number(entry.budget_total) ||
          Number(entry.quantity) * Number(entry.budget_rate)) + total
        : total,
    0
  );

  useEffect(() => {
    if (!packageId || !isAiInProgress) {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      return undefined;
    }
    if (entries?.length || isAiNotFound) return undefined;

    if (pollIntervalRef.current || pollTimeoutRef.current) return undefined;

    pollTimeoutRef.current = setTimeout(() => {
      pollTimeoutRef.current = null;
      const pollStatus = () => {
        if (!isAiInProgressRef.current) return;
        dispatch(
          fetchGenerateBoqStatus({
            packageId,
            pollGeneration: aiPollGenerationRef.current,
          })
        );
      };
      pollStatus();
      pollIntervalRef.current = setInterval(pollStatus, AI_POLL_INTERVAL_MS);
    }, AI_POLL_INITIAL_DELAY_MS);

    return () => {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [dispatch, packageId, isAiInProgress, entries?.length, isAiNotFound]);

  useEffect(() => {
    if (!packageId || !isRefineInProgress) {
      if (refinePollTimeoutRef.current) {
        clearTimeout(refinePollTimeoutRef.current);
        refinePollTimeoutRef.current = null;
      }
      if (refinePollIntervalRef.current) {
        clearInterval(refinePollIntervalRef.current);
        refinePollIntervalRef.current = null;
      }
      return undefined;
    }

    if (refinePollTimeoutRef.current || refinePollIntervalRef.current) {
      return undefined;
    }

    const pollStatus = () => {
      if (!isRefineInProgressRef.current) return;
      dispatch(
        fetchRefineBoqStatus({
          packageId,
          pollGeneration: refinePollGenerationRef.current,
        })
      );
    };

    refinePollTimeoutRef.current = setTimeout(() => {
      refinePollTimeoutRef.current = null;
      pollStatus();
      refinePollIntervalRef.current = setInterval(
        pollStatus,
        REFINE_POLL_INTERVAL_MS
      );
    }, REFINE_POLL_INITIAL_DELAY_MS);

    return () => {
      if (refinePollTimeoutRef.current) {
        clearTimeout(refinePollTimeoutRef.current);
        refinePollTimeoutRef.current = null;
      }
      if (refinePollIntervalRef.current) {
        clearInterval(refinePollIntervalRef.current);
        refinePollIntervalRef.current = null;
      }
    };
  }, [dispatch, packageId, isRefineInProgress]);

  // Analysed instructions are kept in refineById, so the input is freed for the next edit.
  // Failures keep their text so "Try again" resubmits what the user wrote.
  useEffect(() => {
    const requestId = refineJob?.requestId;
    if (
      !requestId ||
      clearedRefinePromptRequestIdRef.current === requestId ||
      !REFINE_ANALYSED_STATE_CODES.includes(refineJob?.stateCode)
    ) {
      return;
    }
    clearedRefinePromptRequestIdRef.current = requestId;
    setRefinePrompt('');
  }, [refineJob?.requestId, refineJob?.stateCode]);

  // On SUCCESS, load API result as draft preview; user saves via PATCH when ready
  useEffect(() => {
    const result = aiStatus?.result;
    if (!id || !isAiSuccess || !result || hasAppliedAiResultRef.current) return;
    if (isAiEmptyResult) return;
    hasAppliedAiResultRef.current = true;

    const previewEntries = mapAiResultEntriesToBoqDraftRows(result?.entries, {
      units,
    });
    const previewNote = mapAiNoteToDraft(result?.notes);

    dispatch(actions.setSelectedEntries({ id, rows: previewEntries }));
    dispatch(actions.setPackageNote({ eid: id, newNote: previewNote }));
    dispatch(actions.setEditingMode({ id, value: true, excel: true }));
    // We can clear AI result after we apply rows to draft.
    dispatch(clearAiGenerationResult({ packageId }));
  }, [
    dispatch,
    actions,
    id,
    isAiSuccess,
    aiStatus?.result,
    slug,
    units,
    packageId,
    isAiEmptyResult,
  ]);

  const persistDraft = () => {
    const data = buildBoqEntityUpdatePayload(nextNote, nextEntries, DELETE_STATUS);
    return dispatch(actions.updateEntity({ id, data }));
  };

  const handleUpdateBoQ = () =>
    persistDraft().then(() =>
      dispatch(actions.fetchBoQList({ slug, releaseBusyForPackageId: id })).then(() => {
        dispatch(actions.setEditingMode({ id, value: true }));
        dispatch(actions.setEntitySaved({ eid: id, value: true }));
      })
    );

  const handleDiscardChanges = () => {
    dispatch(
      actions.setSelectedEntries({
        id,
        rows: Array.isArray(entries)
          ? entries.map((row) => ({ ...row }))
          : [],
      })
    );
    dispatch(
      actions.setPackageNote({
        eid: id,
        newNote: note ? { ...note } : { id: null, text: '' },
      })
    );
    if (readyAfterTemplate) {
      dispatch(actions.setEditingMode({ id, value: true, excel: false }));
    }
  };

  const saveBeforePublishing = () =>
    hasChanges ? persistDraft() : Promise.resolve();

  const handleNewBoq = () =>
    setOpen({
      id: 'new-boq',
      navTitle: 'new-boq-title',
      title: 'new-boq-question',
      cancel: 'cancel',
      confirm: 'confirm',
      backdropClick: true,
      handleAccept: () => {
        setOpen(false);
        stopAiPolling();
        dispatch(clearAiGenerationForPackage({ packageId }));
        const data = buildNewBoqUpdatePayload(nextNote, nextEntries, DELETE_STATUS);
        dispatch(actions.updateEntity({ id, data })).then(() =>
          dispatch(actions.fetchBoQList({ slug, releaseBusyForPackageId: id })).then(() => {
            dispatch(actions.setEditingMode({ id, value: true }));
            dispatch(actions.setEntitySaved({ eid: id, value: true }));
          })
        );
      },
    });

  const handleEdit = () =>
    setOpen({
      id: 'edit-bow',
      navTitle: 'boq-edit-warn-title',
      title: 'boq-edit-warn-body',
      cancel: 'cancel',
      confirm: 'confirm',
      backdropClick: true,
      handleAccept: () => {
        dispatch(actions.setEditingMode({ id, value: true }));
        dispatch(actions.setEntitySaved({ eid: id, value: false }));
        setOpen(false);
      },
    });

  const handlePublish = () => {
    if (has_published_version) {
      return saveBeforePublishing()
        .then(() => dispatch(actions.republishBoQ({ id, reason: '' })))
        .then(() => dispatch(actions.fetchBoQList(slug)))
        .then(() => dispatch(actions.setEditingMode({ id, value: false })));
    }
    return saveBeforePublishing()
      .then(() =>
        dispatch(
          actions.publishBoQ({ id, status: projectStatuses.published.id })
        )
      )
      .then(() => dispatch(actions.fetchBoQList(slug)))
      .then(() => dispatch(actions.setEditingMode({ id, value: false })));
  };

  const isEditable = (params) => {
    if (
      !effectiveEditing ||
      isRefineInProgress ||
      hasPendingRefineReview
    ) {
      return false;
    }
    if (params) {
      const { row, field } = params;
      const { type } = row;
      return type === ITEM || acceptedColumns.includes(field);
    }
    return true;
  };

  const setRows = (rows) => dispatch(actions.setSelectedEntries({ id, rows }));

  const handleSubmitRefinePrompt = (prompt) => {
    const instruction = String(prompt ?? '').trim();
    if (!isRefinePromptReady(instruction)) return;
    setRefinePrompt(instruction);
    dispatch(
      refineBoqWithAI({
        packageId,
        prompt: instruction,
        packageName: entity?.tender?.label || '',
      })
    );
  };

  const handleAcceptRefineRow = (rowId) =>
    dispatch(acceptRefineRow({ packageId, rowId }));

  const handleRejectRefineRow = (rowId) =>
    dispatch(rejectRefineRow({ packageId, rowId }));

  const refineColumnsGrid = (valueOptions) => {
    const baseColumns = columnsGrid(valueOptions);
    if (!hasPendingRefineReview) return baseColumns;
    return buildRefineColumns({
      baseColumns,
      units: valueOptions,
      pendingChanges: pendingRefineChanges,
      onAcceptRow: handleAcceptRefineRow,
      onRejectRow: handleRejectRefineRow,
    });
  };

  const handleRetryAiAnalysis = () => {
    stopAiPolling();
    dispatch(clearAiGenerationForPackage({ packageId }));
  };

  const isNewBoqModal = Boolean(open) && open.id === 'new-boq';
  const isEditWarningModal = Boolean(open) && open.id === 'edit-bow';

  const showEditButton =
    boqStatus.canEdit && !isSmartBoqFailedResult && hasActiveBoqRows(entries);
  const showDraftActions = isDraftState && effectiveEditing;
  const canPublishDraft =
    readyAfterTemplate || saved || hasActiveBoqRows(nextEntries);
  const showRefinePrompt =
    isDraftState &&
    effectiveEditing &&
    hasActiveBoqRows(nextEntries) &&
    !isSmartBoqInProgress &&
    !isAiErrorBlockingBoq;
  const refinePromptDisabled =
    hasEntryChanges ||
    isSmartBoqInProgress ||
    isRefineInProgress ||
    hasPendingRefineReview ||
    isBoqCreationBusy ||
    isPublishing;
  const refineSummary = (() => {
    if (refineJob?.stateCode === REFINE_BOQ_STATE_CODE.NO_CHANGES) {
      const instruction = refineJob?.instruction || '';
      return {
        title: i18next.t('boq-refine-no-changes-title'),
        body: instruction
          ? i18next.t('boq-refine-no-changes-summary', { instruction })
          : i18next.t('boq-refine-no-changes-body'),
      };
    }
    if (refineJob?.stateCode !== REFINE_BOQ_STATE_CODE.REVIEW_COMPLETE) {
      return null;
    }
    const applied = refineJob?.appliedCount || 0;
    const discarded = refineJob?.discardedCount || 0;
    if (!applied) {
      return {
        title: i18next.t('boq-refine-discarded-title'),
        body: i18next.t('boq-refine-discarded-body'),
      };
    }
    return {
      title: i18next.t('boq-refine-applied-title', { count: applied }),
      body: discarded
        ? i18next.t('boq-refine-applied-discarded-body', { discarded })
        : i18next.t('boq-refine-applied-body'),
    };
  })();
  const refineFeedback = {
    [REFINE_BOQ_STATE_CODE.UNSUPPORTED_STRUCTURAL_CHANGES]: {
      titleKey: 'boq-refine-unsupported-title',
      bodyKey: 'boq-refine-unsupported-body',
      canRetry: false,
    },
    [REFINE_BOQ_STATE_CODE.FAILURE]: {
      titleKey: 'boq-refine-error-title',
      bodyKey: 'boq-refine-error-body',
      canRetry: true,
    },
    [REFINE_BOQ_STATE_CODE.NOT_FOUND]: {
      titleKey: 'boq-refine-error-title',
      bodyKey: 'boq-refine-error-body',
      canRetry: true,
    },
    [REFINE_BOQ_STATE_CODE.REQUEST_FAILED]: {
      titleKey: 'boq-refine-error-title',
      bodyKey: 'boq-refine-error-body',
      canRetry: true,
    },
    [REFINE_BOQ_STATE_CODE.POLL_FAILED]: {
      titleKey: 'boq-refine-error-title',
      bodyKey: 'boq-refine-error-body',
      canRetry: true,
    },
    [REFINE_BOQ_STATE_CODE.INVALID_RESPONSE]: {
      titleKey: 'boq-refine-error-title',
      bodyKey: 'boq-refine-error-body',
      canRetry: true,
    },
  }[refineJob?.stateCode];
  const hasApiErrorMessage = isUserFacingRefineErrorMessage(
    refineJob?.apiErrorMessage
  );
  const refineFeedbackTitleKey =
    refineFeedback && hasApiErrorMessage && isRefineValidationError(refineJob)
      ? 'boq-refine-validation-title'
      : refineFeedback?.titleKey;
  let refineFeedbackBody = null;
  if (refineFeedback) {
    refineFeedbackBody = hasApiErrorMessage
      ? refineJob.apiErrorMessage
      : i18next.t(refineFeedback.bodyKey);
  }

  const handleDismissRefineFeedback = () => {
    dispatch(clearRefineBoqForPackage({ packageId }));
  };

  // "Try again" resubmits the instruction that failed; unsupported results only dismiss.
  const handleRetryRefineFeedback = () => {
    const retryInstruction = (
      refineJob?.instruction ||
      refinePrompt ||
      ''
    ).trim();
    const canRetry =
      Boolean(refineFeedback?.canRetry) &&
      REFINE_RETRYABLE_STATE_CODES.includes(refineJob?.stateCode) &&
      isRefinePromptReady(retryInstruction) &&
      !refinePromptDisabled;
    dispatch(clearRefineBoqForPackage({ packageId }));
    if (canRetry) handleSubmitRefinePrompt(retryInstruction);
  };

  let confirmModalAcceptSx = {};
  let confirmModalCancelSx = {};
  if (isNewBoqModal || isEditWarningModal) {
    confirmModalAcceptSx = newBoqModalAcceptSx;
    confirmModalCancelSx = newBoqModalCancelSx;
  }

  return (
    <>
      <Modal
        open={open}
        setOpen={setOpen}
        style={modalSx}
        acceptStyleProp={confirmModalAcceptSx}
        cancelStyleProp={confirmModalCancelSx}
        titleStyleProp={isNewBoqModal ? newBoqModalTitleSx : {}}
        navTitleColor={isNewBoqModal ? boqAccent : undefined}
      />
      {showRefinePrompt && (
        <RefinePromptCard
          value={refinePrompt}
          onChange={setRefinePrompt}
          onSubmit={handleSubmitRefinePrompt}
          disabled={refinePromptDisabled}
          unsavedChanges={hasEntryChanges && !isRefineInProgress}
          applying={isRefineInProgress}
        />
      )}
      {showRefinePrompt && refineFeedback && (
        <Box sx={boqAiInlineAlertBannerSx}>
          <Box sx={boqAiInlineAlertIconBoxSx}>
            <InfoOutlinedIcon sx={{ color: clinkRed, fontSize: 20 }} />
          </Box>
          <Box sx={boqAiInlineAlertTextColSx}>
            <Typography sx={boqAiInlineAlertTitleTypographySx}>
              {i18next.t(refineFeedbackTitleKey)}
            </Typography>
            <Typography sx={boqAiInlineAlertBodyTypographySx}>
              {refineFeedbackBody}
            </Typography>
          </Box>
          <Box sx={boqAiInlineAlertActionsSx}>
            <Button
              size="small"
              variant="text"
              onClick={handleDismissRefineFeedback}
            >
              {i18next.t('boq-refine-dismiss')}
            </Button>
            {refineFeedback.canRetry && (
              <Button
                size="small"
                variant="outlined"
                onClick={handleRetryRefineFeedback}
              >
                {i18next.t('boq-refine-try-again')}
              </Button>
            )}
          </Box>
        </Box>
      )}
      {showRefinePrompt && refineSummary && (
        <RefineSummaryBanner
          title={refineSummary.title}
          body={refineSummary.body}
          onDismiss={() => dispatch(clearRefineBoqForPackage({ packageId }))}
        />
      )}
      {showRefinePrompt && hasPendingRefineReview && (
        <RefineChangesBanner
          count={refineReviewRows.length}
          instruction={refineReview?.instruction || refinePrompt}
          onRejectAll={() =>
            dispatch(rejectAllRefineRows({ packageId }))
          }
          onAcceptAll={() =>
            dispatch(acceptAllRefineRows({ packageId }))
          }
        />
      )}
      <Wrapper
        headerTitle={
          !isSmartBoqInProgress && !isAiErrorBlockingBoq ? (
            <Box sx={boqHeaderTitleSx}>
              <Typography sx={boqHeaderTitleTypographySx}>
                {i18next.t('boq-digital-price-breakdown')}
              </Typography>
              {boqStatus.labelKey ? (
                <Chip
                  size="small"
                  label={i18next.t(boqStatus.labelKey)}
                  sx={getBoqStatusChipSx(boqStatus.tone)}
                />
              ) : null}
            </Box>
          ) : null
        }
        contentHeader={
          !(isSmartBoqInProgress || isAiErrorBlockingBoq) ? (
            <Box sx={boqContentHeaderActionsSx}>
              {showEditButton && (
                <Button
                  sx={boqEditButtonSx}
                  onClick={handleEdit}
                  variant="contained"
                  disabled={headerActionsDisabled}
                >
                  {i18next.t('boq-edit-action')}
                </Button>
              )}
              {showDraftActions && (
                <Button
                  sx={boqNewButtonSx}
                  onClick={handleNewBoq}
                  variant="outlined"
                  color="secondary"
                  disabled={headerActionsDisabled}
                >
                  {i18next.t('boq-upload-new')}
                </Button>
              )}
              {showDraftActions && !isSmartBoqFailedResult && (
                <Button
                  color="primary"
                  variant="contained"
                  onClick={handlePublish}
                  sx={{ ...boqPublishButtonSx, textWrap: 'nowrap' }}
                  disabled={
                    headerActionsDisabled || !canPublishDraft
                  }
                >
                  {i18next.t('boq-save-and-publish')}
                </Button>
              )}
            </Box>
          ) : null
        }
      >
        {boqStatus.noticeKey &&
          !isSmartBoqInProgress &&
          !isAiErrorBlockingBoq && (
            <Box sx={{ ...boqCardSectionSx, p: 0 }}>
              <BoqStatusNotice
                status={boqStatus}
                showDiscard={
                  showDraftActions &&
                  !isSmartBoqFailedResult &&
                  hasChanges
                }
                discardDisabled={headerActionsDisabled}
                onDiscard={handleDiscardChanges}
                onSaveDraft={handleUpdateBoQ}
              />
            </Box>
          )}
        {isAiEmptyResult && (
          <Box data-testid="ai-empty-result-banner" sx={boqAiInlineAlertBannerSx}>
            <Box sx={boqAiInlineAlertIconBoxSx}>
              <InfoOutlinedIcon sx={{ color: clinkRed, fontSize: 20 }} />
            </Box>
            <Box sx={boqAiInlineAlertTextColSx}>
              <Typography sx={boqAiInlineAlertTitleTypographySx}>
                {i18next.t('boq-smart-builder-empty-title')}
              </Typography>
              <Typography sx={boqAiInlineAlertBodyTypographySx}>
                {i18next.t('boq-smart-builder-empty-body')}
              </Typography>
            </Box>
          </Box>
        )}
        {isAiFailure && (
          <Box data-testid="ai-failure-banner" sx={boqAiInlineAlertBannerSx}>
            <Box sx={boqAiInlineAlertIconBoxSx}>
              <InfoOutlinedIcon sx={{ color: clinkRed, fontSize: 20 }} />
            </Box>
            <Box sx={boqAiInlineAlertTextColSx}>
              <Typography sx={boqAiInlineAlertTitleTypographySx}>
                {i18next.t('boq-smart-builder-failure-title')}
              </Typography>
              <Typography sx={boqAiInlineAlertBodyTypographySx}>
                {i18next.t('boq-smart-builder-empty-body')}
              </Typography>
            </Box>
          </Box>
        )}
        {!isSmartBoqInProgress && !isAiErrorBlockingBoq && (
          <Box sx={{ ...boqCardSectionSx, p: 4, pt: 2 }}>
            <NoteTextarea
              editing={effectiveEditing && !isSmartBoqInProgress}
              note={nextNote}
              dispatch={(value) => dispatch(actions.setPackageNote(value))}
              style={inputBaseSx(
                palette,
                !effectiveEditing || isSmartBoqInProgress
              )}
              id={id}
            />
          </Box>
        )}
        {isAiInProgress && (
          <Box sx={{ ...boqCardSectionSx, px: 4, pb: 4 }}>
            <Box
              sx={{
                p: 3,
              }}
            >
              <Typography
                sx={{
                  fontSize: '20px',
                  fontWeight: 800,
                  color: boqAccent,
                  mb: 1,
                }}
              >
                {i18next.t('boq-smart-builder-analysis-title')}
              </Typography>
              <Typography sx={{ fontSize: '13px', color: black, opacity: 0.7 }}>
                {i18next.t('boq-smart-builder-analysis-desc')}
              </Typography>

              <Typography sx={{ fontSize: '13px', color: black, opacity: 0.7, mt: 2 }}>
                {i18next.t('boq-smart-builder-analysis-takes')}
              </Typography>
              <Box
                component="ul"
                sx={{
                  mt: 1,
                  mb: 2,
                  pl: 2.5,
                  ml: 0.5,
                  color: black,
                  opacity: 0.75,
                  fontSize: '13px',
                  listStyleType: 'disc',
                  listStylePosition: 'outside',
                  '& li': {
                    display: 'list-item',
                    paddingLeft: '0.25rem',
                    marginBottom: '0.25rem',
                  },
                }}
              >
                <li>{i18next.t('boq-smart-builder-analysis-bullet-1')}</li>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CircularProgress size={16} sx={{ color: boqAccent }} />
                <Typography sx={{ fontSize: '13px', color: black, opacity: 0.7 }}>
                  {i18next.t('boq-smart-builder-analysis-loading')}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography sx={{ fontSize: '13px', color: black, opacity: 0.7 }}>
                  {i18next.t('boq-smart-builder-step-of', {
                    current: progressMeta.current,
                    total: progressMeta.total,
                  })}
                </Typography>
                <Typography sx={{ fontSize: '13px', color: black, opacity: 0.7 }}>
                  {progressMeta.pct}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={progressMeta.pct}
                sx={{
                  height: 8,
                  borderRadius: 8,
                  backgroundColor: clinkLightPurple,
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: boqAccent,
                    borderRadius: 8,
                  },
                }}
              />
            </Box>
          </Box>
        )}

        {isAiErrorBlockingBoq && (
          <Box sx={{ ...boqCardSectionSx, px: 4, pb: 4 }}>
            <Box data-testid="ai-service-error-panel" sx={boqAiServiceErrorPanelWrapperSx}>
              <Box sx={boqAiServiceErrorCardSx}>
                <Box sx={boqAiServiceErrorIconCircleSx}>
                  <WarningAmberIcon sx={{ color: clinkRed, fontSize: 22 }} />
                </Box>
                <Box sx={boqAiServiceErrorTextColSx}>
                  <Box component="span" sx={boqAiServiceErrorBadgeSx}>
                    {i18next.t('boq-smart-builder-ai-service-badge')}
                  </Box>
                  <Typography sx={boqAiServiceErrorTitleTypographySx}>
                    {i18next.t('boq-smart-builder-service-error-title')}
                  </Typography>
                  <Typography sx={boqAiServiceErrorSubtitleTypographySx}>
                    {i18next.t('boq-smart-builder-service-error-subtitle')}
                  </Typography>
                </Box>
              </Box>

              <Box sx={boqAiServiceErrorSuggestionsWrapSx}>
                <Typography sx={boqAiServiceErrorSuggestionsHeadingSx}>
                  {i18next.t('boq-smart-builder-service-error-suggestions-heading')}
                </Typography>
                <Box component="ul" sx={boqAiServiceErrorSuggestionsListSx}>
                  <li>{i18next.t('boq-smart-builder-service-error-suggestion-1')}</li>
                  <li>{i18next.t('boq-smart-builder-service-error-suggestion-2')}</li>
                </Box>
              </Box>

              <Box sx={boqAiServiceErrorRetryRowSx}>
                <Button
                  variant="contained"
                  onClick={handleRetryAiAnalysis}
                  sx={boqAiServiceErrorRetryButtonSx}
                >
                  {i18next.t('boq-smart-builder-retry-boq')}
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {!isAiInProgress && !isAiErrorBlockingBoq && (
          <>
            <Box sx={boqCardSectionSx}>
              <Box
                id="datagrid-wrapper"
                sx={{
                  width: '100%',
                  pr: 1,
                }}
              >
                {showBoqGridSkeleton ? (
                  <Box sx={{ pl: 1 }}>
                    <Skeleton
                      variant="rectangular"
                      height={44}
                      sx={{ borderRadius: '8px 8px 0 0' }}
                    />
                    <Skeleton
                      variant="rectangular"
                      height={420}
                      sx={{ borderRadius: '0 0 8px 8px', mt: 0.5 }}
                    />
                  </Box>
                ) : (
                  <DataGrid
                  useRows={[nextEntries, setRows]}
                  autoHeight
                  showColumnVerticalBorder={false}
                  getRowHeight={(params) =>
                    estimateBoqRowHeight(params.model) +
                    (pendingRefineChanges[String(params?.model?.id)] ? 24 : 0)
                  }
                  columns={refineColumnsGrid}
                  entries={entries}
                  units={units}
                  noAction={hasPendingRefineReview}
                  rowReordering={
                    effectiveEditing &&
                    !isRefineInProgress &&
                    !hasPendingRefineReview
                  }
                  isCellEditable={isEditable}
                  sx={getBoqContentDataGridSx({
                    boqSectionBg,
                    borderbox,
                    darkCharcoal,
                    eerieBlack,
                    white,
                  })}
                  getRowClassName={(params) => {
                    let extraClasses = '';
                    if (params) {
                      const { row } = params;
                      const item_version = row?.item_version;
                      const status = item_version?.status;
                      extraClasses =
                        Number(status) === DELETE_STATUS ? ' hide-row' : '';
                    }
                    const pendingClass = pendingRefineChanges[
                      String(params?.row?.id)
                    ]
                      ? ' boq--refine-pending'
                      : '';
                    return `boq--${params.row.type}${extraClasses}${pendingClass}`;
                  }}
                  getCellClassName={(params) => {
                    if (params) {
                      const { row, field } = params;
                      const { type } = row;
                      const changedCell = Boolean(
                        pendingRefineChanges[String(row?.id)]?.fields?.[field]
                      );
                      const showCell =
                        field === '__reorder__' ||
                        field === 'refine_actions' ||
                        field === 'description' ||
                        type === ITEM ||
                        (acceptedColumns.includes(field) &&
                          type === GROUPED_HEADING) ||
                        (sectionAcceptedColumns.includes(field) &&
                          type === SECTION);
                      const priceCell =
                        field === 'budget_rate' || field === 'budget_total';
                      if (showCell && priceCell) {
                        return `show price-cell${
                          changedCell ? ' boq--refine-changed' : ''
                        }`;
                      }
                      if (showCell) {
                        return `show${
                          changedCell ? ' boq--refine-changed' : ''
                        }`;
                      }
                    }
                    return 'hide';
                  }}
                />
                )}
              </Box>
            </Box>
            <Box sx={boqCardSectionSx}>
              <Footer total={totalPrice} editing={effectiveEditing} />
            </Box>
          </>
        )}
      </Wrapper>
    </>
  );
};

const EnableChecker = (props) => {
  if (!props.enable) {
    return null;
  }

  return <Content {...props} />;
}

const mapStateToProps = (state) => {
  return {
    boq: state.boq,
  };
};

export default connect(mapStateToProps)(EnableChecker);
